"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import { motion, useReducedMotion } from "framer-motion";

const STEP_ADVANCE_MS = 2400;
const NODE_SIZE = 36;
const STROKE_WIDTH = 2.5;
const RADIUS = (NODE_SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export type WelcomeWorkflowProgressProps = {
  steps: string[];
  startDelay?: number;
  className?: string;
};

type StepState = "pending" | "active" | "complete";

function stepStateForIndex(index: number, activeStep: number, stepCount: number): StepState {
  if (activeStep >= stepCount) return "complete";
  if (index < activeStep) return "complete";
  if (index === activeStep) return "active";
  return "pending";
}

function ProgressConnector({ filled, animateIn }: { filled: boolean; animateIn: boolean }) {
  return (
    <div
      className="relative h-0.5 w-full overflow-hidden rounded-full bg-white/[0.08]"
      aria-hidden
    >
      <motion.div
        className="absolute inset-y-0 left-0 rounded-full bg-indigo-400/70"
        initial={{ width: "0%" }}
        animate={{ width: filled ? "100%" : "0%" }}
        transition={
          animateIn && filled
            ? { duration: 0.65, ease: [0.22, 1, 0.36, 1] }
            : { duration: 0 }
        }
      />
    </div>
  );
}

function ProgressNode({
  index,
  state,
  fillDurationMs,
}: {
  index: number;
  state: StepState;
  fillDurationMs: number;
}) {
  const reduceMotion = useReducedMotion();

  if (state === "complete") {
    return (
      <motion.div
        className="flex size-9 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white shadow-[0_0_0_4px_rgba(16,185,129,0.16)]"
        initial={reduceMotion ? false : { scale: 0.82, opacity: 0.7 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        aria-hidden
      >
        <FontAwesomeIcon icon={faCheck} className="size-3.5" />
      </motion.div>
    );
  }

  if (state === "pending") {
    return (
      <div
        className="flex size-9 shrink-0 items-center justify-center rounded-full text-xs font-semibold tabular-nums text-slate-500 ring-1 ring-slate-500/35"
        aria-hidden
      >
        {index + 1}
      </div>
    );
  }

  return (
    <div className="relative flex size-9 shrink-0 items-center justify-center" aria-hidden>
      <svg
        className="absolute inset-0 size-full -rotate-90"
        viewBox={`0 0 ${NODE_SIZE} ${NODE_SIZE}`}
      >
        <circle
          cx={NODE_SIZE / 2}
          cy={NODE_SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth={STROKE_WIDTH}
          className="text-slate-500/35"
        />
        <motion.circle
          cx={NODE_SIZE / 2}
          cy={NODE_SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          className="text-emerald-400"
          strokeDasharray={CIRCUMFERENCE}
          initial={{ strokeDashoffset: CIRCUMFERENCE }}
          animate={{ strokeDashoffset: 0 }}
          transition={
            reduceMotion
              ? { duration: 0 }
              : { duration: fillDurationMs / 1000, ease: "linear" }
          }
        />
      </svg>
      <span className="relative text-xs font-semibold tabular-nums text-white">{index + 1}</span>
    </div>
  );
}

/** Animated workflow steps for the workspace welcome screen. */
export function WelcomeWorkflowProgress({
  steps,
  startDelay = 0,
  className,
}: WelcomeWorkflowProgressProps) {
  const reduceMotion = useReducedMotion();
  const [activeStep, setActiveStep] = useState(0);
  const [visible, setVisible] = useState(reduceMotion ?? false);
  const [animatedConnectors, setAnimatedConnectors] = useState<Set<number>>(() => new Set());
  const stepCount = steps.length;

  useEffect(() => {
    if (reduceMotion) {
      setVisible(true);
      setActiveStep(stepCount);
      return;
    }

    const revealTimer = window.setTimeout(() => setVisible(true), startDelay * 1000);
    return () => window.clearTimeout(revealTimer);
  }, [reduceMotion, startDelay, stepCount]);

  useEffect(() => {
    if (!visible || reduceMotion || stepCount === 0) return;

    setActiveStep(0);
    setAnimatedConnectors(new Set());

    const timers = Array.from({ length: stepCount }, (_, index) =>
      window.setTimeout(() => {
        if (index < stepCount - 1) {
          setActiveStep(index + 1);
          setAnimatedConnectors(new Set(Array.from({ length: index + 1 }, (_, connectorIndex) => connectorIndex + 1)));
        } else {
          setActiveStep(stepCount);
        }
      }, STEP_ADVANCE_MS * (index + 1)),
    );

    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [visible, reduceMotion, stepCount]);

  if (stepCount === 0) return null;

  if (!visible) {
    return <div className={clsx("mt-8 h-[4.5rem] w-full", className)} aria-hidden />;
  }

  return (
    <motion.div
      className={clsx("mt-8 w-full", className)}
      initial={reduceMotion ? false : { opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.65, ease: "easeOut" }}
      data-testid="workspace-welcome-progress"
      aria-label="Workflow overview"
    >
      <div className="flex w-full items-start px-1 sm:px-3">
        {steps.map((label, index) => {
          const state = stepStateForIndex(index, activeStep, stepCount);

          return (
            <div key={`${label}-${index}`} className="contents">
              <div className="flex w-[5.5rem] shrink-0 flex-col items-center gap-2.5 sm:w-[6.25rem]">
                <ProgressNode index={index} state={state} fillDurationMs={STEP_ADVANCE_MS} />
                <p className="max-w-[6.5rem] text-center text-[11px] leading-snug text-white sm:max-w-[7rem] sm:text-xs">
                  {label}
                </p>
              </div>

              {index < steps.length - 1 ? (
                <div className="flex min-w-[2.5rem] flex-1 items-center self-start px-1 pt-[1.125rem] sm:min-w-[3.5rem] sm:px-2">
                  <ProgressConnector
                    filled={activeStep > index}
                    animateIn={animatedConnectors.has(index + 1)}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}
